import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/admin/AdminLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { Skeleton } from '../../components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { ArrowLeft, Mail, Phone, Building2, Save } from 'lucide-react';
import { toast } from 'sonner';
import { adminApi } from '../../lib/admin-api';
import { formatDateTime, statusBadgeClass } from './formatters';

const STATUS_OPTIONS = ['new', 'contacted', 'in-progress', 'converted', 'archived'];

function Field({ label, value }) {
  return (
    <div>
      <p className="font-mono text-[10px] tracking-[0.22em] uppercase text-muted-foreground mb-1">{label}</p>
      <p className="text-sm text-foreground">{value || <span className="text-muted-foreground italic">—</span>}</p>
    </div>
  );
}

export default function AdminContactDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [statusDraft, setStatusDraft] = useState('new');
  const [noteDraft, setNoteDraft] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const response = await adminApi.get(`/contacts/${id}`);
        if (!cancelled) {
          setData(response.data);
          setStatusDraft(response.data.status || 'new');
        }
      } catch (_e) {
        if (!cancelled) {
          toast.error('Failed to load contact');
          navigate('/admin/contacts');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, navigate]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = { status: statusDraft };
      if (noteDraft.trim()) payload.note = noteDraft.trim();
      const response = await adminApi.patch(`/contacts/${id}`, payload);
      setData(response.data);
      setNoteDraft('');
      toast.success(`Contact marked as “${statusDraft}”`);
    } catch (error) {
      const detail = error?.response?.data?.detail || 'Update failed';
      toast.error(detail);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout title="Contact">
        <Skeleton className="h-64 w-full" />
      </AdminLayout>
    );
  }
  if (!data) return null;

  const dirty = statusDraft !== (data.status || 'new') || noteDraft.trim().length > 0;
  const notes = Array.isArray(data.notes) ? data.notes : [];

  return (
    <AdminLayout>
      <Link
        to="/admin/contacts"
        className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground mb-5"
        data-testid="admin-contact-back"
      >
        <ArrowLeft className="w-3.5 h-3.5" />
        Back to contacts
      </Link>

      <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-semibold tracking-tight text-foreground" data-testid="admin-contact-name">
            {data.name}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            {data.company || 'Individual'} · Submitted {formatDateTime(data.submitted_at)}
          </p>
        </div>
        <Badge
          variant="secondary"
          className={`font-mono text-[11px] tracking-[0.18em] uppercase px-3 py-1.5 ${statusBadgeClass(data.status)}`}
          data-testid="admin-contact-current-status"
        >
          {data.status || 'new'}
        </Badge>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 space-y-5">
          <Card className="editorial-card p-0">
            <div className="spec-strip" />
            <CardContent className="p-6 pt-7">
              <p className="font-mono text-[10px] tracking-[0.22em] uppercase text-muted-foreground mb-4">Contact</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <Field label="Name" value={data.name} />
                <Field label="Company" value={data.company ? (
                  <span className="inline-flex items-center gap-1.5">
                    <Building2 className="w-3.5 h-3.5" />
                    {data.company}
                  </span>
                ) : null} />
                <Field label="Email" value={
                  <a href={`mailto:${data.email}`} className="hover:text-primary inline-flex items-center gap-1.5">
                    <Mail className="w-3.5 h-3.5" />
                    {data.email}
                  </a>
                } />
                <Field label="Phone" value={
                  <a href={`tel:${data.phone}`} className="hover:text-primary inline-flex items-center gap-1.5">
                    <Phone className="w-3.5 h-3.5" />
                    {data.phone}
                  </a>
                } />
              </div>
            </CardContent>
          </Card>

          <Card className="editorial-card p-0">
            <CardContent className="p-6">
              <p className="font-mono text-[10px] tracking-[0.22em] uppercase text-muted-foreground mb-4">Inquiry</p>
              <div className="space-y-5">
                <Field label="Project Type" value={data.project_type} />
                <div>
                  <p className="font-mono text-[10px] tracking-[0.22em] uppercase text-muted-foreground mb-1">Message</p>
                  <p className="text-sm text-foreground whitespace-pre-wrap leading-relaxed">
                    {data.message || <span className="text-muted-foreground italic">No message provided.</span>}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="space-y-5">
          <Card className="editorial-card p-0" data-testid="admin-contact-status-panel">
            <CardContent className="p-6">
              <p className="font-mono text-[10px] tracking-[0.22em] uppercase text-muted-foreground mb-3">Status &amp; Note</p>
              <Select value={statusDraft} onValueChange={setStatusDraft}>
                <SelectTrigger data-testid="admin-contact-status-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STATUS_OPTIONS.map((option) => (
                    <SelectItem key={option} value={option}>
                      {option.charAt(0).toUpperCase() + option.slice(1)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Textarea
                className="mt-3 min-h-[100px] resize-none"
                value={noteDraft}
                onChange={(event) => setNoteDraft(event.target.value)}
                placeholder="Optional note (e.g., 'Quoted $48k, follow up next week')"
                data-testid="admin-contact-note"
              />
              <Button
                className="w-full mt-3 gap-1.5"
                disabled={!dirty || saving}
                onClick={handleSave}
                data-testid="admin-contact-save"
              >
                <Save className="w-3.5 h-3.5" />
                {saving ? 'Saving…' : 'Save'}
              </Button>
            </CardContent>
          </Card>

          <Card className="editorial-card p-0">
            <CardContent className="p-6">
              <p className="font-mono text-[10px] tracking-[0.22em] uppercase text-muted-foreground mb-3">History</p>
              {notes.length === 0 ? (
                <p className="text-xs text-muted-foreground italic">No notes yet.</p>
              ) : (
                <ul className="space-y-3">
                  {[...notes].reverse().map((note, index) => (
                    <li key={note.at || index} className="text-xs">
                      <p className="text-muted-foreground font-mono">
                        {formatDateTime(note.at)} — {note.by}
                      </p>
                      <p className="text-foreground/80 mt-0.5 whitespace-pre-wrap">{note.text}</p>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </AdminLayout>
  );
}
